import React, { useState, useMemo } from "react";
import { Copy, CheckCircle2, MessageSquare, AlertCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * TemplateSignatureGuide
 *
 * Meta approves Twilio Content Template bodies once — they cannot be modified
 * at runtime. To include the clinic signature in every patient WhatsApp
 * message, the admin must paste the recommended body texts (with the
 * signature already appended) into the Twilio Content Editor and resubmit
 * for approval.
 *
 * This component shows the 4 recommended template bodies with the CURRENT
 * `wa_signature` value already in place, and gives a one-click copy button
 * per template. Each body follows the variables that DentArt already sends
 * — see `notify_doctor_new_appointment`, `send_daily_agenda`, appointment
 * reminders, doctor change notifications, and confirmation flow in server.py.
 */
export default function TemplateSignatureGuide({ signature }) {
  const sig = (signature || "").trim();
  const sigLine = sig ? `\n\n— ${sig}` : "";

  const templates = useMemo(() => [
    {
      key: "appointment",
      title: "Konfirmim Takimi",
      settingField: "twilio_appointment_template_sid",
      language: "sq",
      category: "Utility",
      variables: "{{1}}=Emri pacientit, {{2}}=Klinika, {{3}}=Data/ora",
      body: `Përshëndetje {{1}}, ju konfirmojmë takimin tuaj në {{2}} për {{3}}. Ju presim!${sigLine}`,
    },
    {
      key: "reminder",
      title: "Kujtues 24h",
      settingField: "twilio_reminder_template_sid",
      language: "sq",
      category: "Utility",
      variables: "{{1}}=Emri pacientit, {{2}}=Data/ora, {{3}}=Klinika",
      body: `🦷 Kujtues: Nesër keni takim në {{3}} për {{2}}. Përgjigjuni "OK" për konfirmim ose kërkoni ndryshim.${sigLine}`,
    },
    {
      key: "doctor_alert",
      title: "Lajmërim Doktori — Takim i Ri",
      settingField: "twilio_doctor_alert_template_sid",
      language: "sq",
      category: "Utility",
      variables: "{{1}}=Emri pacientit, {{2}}=Telefoni, {{3}}=Simptomat, {{4}}=Data/ora",
      body: `🦷 Takim i ri — Pacienti: {{1}}, Tel: {{2}}, Arsyeja: {{3}}, Ora: {{4}}${sigLine}`,
    },
    {
      key: "doctor_agenda",
      title: "Axhendë Ditore për Doktorin (08:00)",
      settingField: "twilio_doctor_agenda_template_sid",
      language: "sq",
      category: "Utility",
      variables: "{{1}}=Data, {{2}}=Lista e takimeve, {{3}}=Numri total",
      body: `📅 Axhenda për {{1}} ({{3}} takim/e): {{2}}${sigLine}`,
    },
    {
      key: "doctor_change",
      title: "Ndryshim Takimi (Anulim/Spostim)",
      settingField: "twilio_doctor_change_template_sid",
      language: "sq",
      category: "Utility",
      variables: "{{1}}=Emri pacientit, {{2}}=ANULUAR/SPOSTUAR/FSHIRE, {{3}}=Ora e vjetër, {{4}}=Ora e re",
      body: `⚠️ Ndryshim takimi — Pacienti {{1}} · Veprim: {{2}} · Nga: {{3}} · Në: {{4}}${sigLine}`,
    },
  ], [sigLine]);

  const [copied, setCopied] = useState(null);

  const copyBody = async (key, body) => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(key);
      toast.success("Trupi u kopjua në clipboard");
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 2500);
    } catch {
      toast.error("Nuk u kopjua dot — kopjojeni manualisht");
    }
  };

  return (
    <div className="rounded-lg bg-[#FDFBF7] p-4 border border-[#E2DFD8] space-y-4">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#2C4C3B] text-[#FDFBF7]">
          <MessageSquare size={16} />
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-semibold tracking-tight">Nënshkrimi te Template-t (Meta-approved)</h4>
          <p className="mt-1 text-xs text-[#5C5C5C] leading-relaxed">
            Twilio Content Templates janë trupe të miratuara nga Meta dhe nuk mund të modifikohen në runtime.
            Për ta bërë që nënshkrimi <b>t'i vijë çdo pacienti në çdo mesazh</b> (reminders, konfirmime, njoftime),
            duhet ta përfshini në trupin e çdo template-i kur e krijoni ose e përditësoni te Twilio Content Editor.
          </p>
        </div>
      </div>

      {!sig && (
        <div className="flex items-start gap-2 rounded-md border border-[#D4A24C]/50 bg-[#D4A24C]/10 px-3 py-2 text-xs text-[#8B6914]">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>Nënshkrimi është bosh — vendoseni tek kutia më sipër dhe pastaj kopjoni trupat e template-eve.</span>
        </div>
      )}

      <div className="space-y-3">
        {templates.map((t) => (
          <div key={t.key} className="rounded-md border border-[#E2DFD8] bg-white p-3"
               data-testid={`template-guide-${t.key}`}>
            <div className="flex items-baseline justify-between gap-2 mb-2 flex-wrap">
              <div>
                <div className="text-sm font-semibold text-[#1A1A1A]">{t.title}</div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C5C5C]">
                  Category: {t.category} · Language: {t.language}
                </div>
              </div>
              <button
                type="button"
                onClick={() => copyBody(t.key, t.body)}
                className="inline-flex items-center gap-1 rounded-md bg-[#2C4C3B] text-[#FDFBF7] px-2.5 py-1 text-[11px] hover:bg-[#1F3628]"
                data-testid={`copy-template-${t.key}`}
              >
                {copied === t.key ? <><CheckCircle2 size={12} /> U kopjua</> : <><Copy size={12} /> Kopjo trupin</>}
              </button>
            </div>
            <pre className="bg-[#FDFBF7] rounded p-2 text-[11px] font-mono whitespace-pre-wrap break-words border border-[#E2DFD8]">{t.body}</pre>
            <div className="mt-1.5 text-[10px] text-[#5C5C5C]">
              Variablat: <code className="text-[#2C4C3B]">{t.variables}</code>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-md bg-[#2C4C3B]/5 border border-[#2C4C3B]/20 p-3 text-xs text-[#1A1A1A] leading-relaxed">
        <b>Si të aplikohet për çdo template:</b>
        <ol className="list-decimal ml-5 mt-1 space-y-1">
          <li>Hyni te <a href="https://console.twilio.com/us1/develop/sms/content-editor" target="_blank" rel="noreferrer" className="underline text-[#2C4C3B] font-medium">Twilio Content Editor</a></li>
          <li>Zgjidh template-in ekzistues (ose krijo një të ri me këtë emër)</li>
          <li>Kliko <b>Duplicate</b> → ngjis trupin e ri me nënshkrim (kopjuar sipër)</li>
          <li>Submit for WhatsApp approval — Meta e miraton zakonisht brenda 1-2 orëve për Utility</li>
          <li>Kur SID-i i ri të miratohet, ngjiseni te fusha përkatëse më lart</li>
        </ol>
        <p className="mt-2">
          <b>Pas kësaj:</b> çdo mesazh që sistemi u dërgon pacientëve — brenda ose jashtë 24h window — do të përfundojë me nënshkrimin tuaj.
        </p>
      </div>
    </div>
  );
}
